import React from 'react'
import {
    getAssignableTeachersForCard,
    getAssignedTeachersForCard,
    resolveTeacherImageUrl,
} from '../../StudentOnboardingFall2023/teacherAssignmentUtils'
import './teacher-assignment-field.css'

const getTeacherInitials = (teacherName) => String(teacherName || 'Teacher')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((namePart) => namePart[0])
    .join('')
    .toUpperCase()

const TeacherAssignmentField = ({
    card,
    teachers = [],
    loadingTeachers = false,
    saving = false,
    error = '',
    onUpdate,
}) => {
    const assignedTeachers = getAssignedTeachersForCard(card, teachers)
    const assignedTeacherIds = assignedTeachers.map((teacher) =>
        String(teacher.teacherId || '').trim()
    )
    const assignedTeacherIdSet = new Set(assignedTeacherIds)
    const availableTeachers = getAssignableTeachersForCard(teachers, card)
        .filter((teacher) =>
            !assignedTeacherIdSet.has(String(teacher.TeacherID || '').trim())
        )

    const addTeacher = (event) => {
        const teacherId = String(event.target.value || '').trim()
        if (!teacherId || assignedTeacherIdSet.has(teacherId) || saving) return
        onUpdate([...assignedTeacherIds, teacherId])
    }

    const removeTeacher = (teacherId) => {
        if (saving) return
        onUpdate(
            assignedTeacherIds.filter(
                (assignedTeacherId) => assignedTeacherId !== teacherId
            )
        )
    }

    const emptyOptionLabel = () => {
        if (loadingTeachers) return 'Loading teachers…'
        if (saving) return 'Saving assignment…'
        if (availableTeachers.length > 0) return 'Add teacher…'
        if (assignedTeachers.length > 0) return 'All school teachers assigned'
        return 'No teachers available'
    }

    return (
        <section className='teacher-assignment-field'>
            <div className='teacher-assignment-field__header'>
                <div>
                    <div className='teacher-assignment-field__label'>Assigned teachers</div>
                    <div className='teacher-assignment-field__description'>
                        Add one or more teachers for this family.
                    </div>
                </div>
                <select
                    className='teacher-assignment-field__select'
                    aria-label={`Add a teacher to ${card.todoName}`}
                    value=''
                    disabled={
                        loadingTeachers
                        || saving
                        || availableTeachers.length === 0
                    }
                    onChange={addTeacher}
                >
                    <option value=''>{emptyOptionLabel()}</option>
                    {availableTeachers.map((teacher) => (
                        <option key={teacher.TeacherID} value={teacher.TeacherID}>
                            {teacher.Name}
                        </option>
                    ))}
                </select>
            </div>

            {assignedTeachers.length > 0 ? (
                <div className='teacher-assignment-field__teachers'>
                    {assignedTeachers.map((teacher) => {
                        const teacherId = String(teacher.teacherId || '').trim()
                        const teacherName = teacher.teacherName || 'Assigned teacher'
                        const imageUrl = resolveTeacherImageUrl(teacher.imageName)

                        return (
                            <div className='teacher-assignment-field__teacher' key={teacherId}>
                                {imageUrl ? (
                                    <img
                                        className='teacher-assignment-field__teacher-image'
                                        src={imageUrl}
                                        alt={teacherName}
                                    />
                                ) : (
                                    <span className='teacher-assignment-field__teacher-initials'>
                                        {getTeacherInitials(teacherName)}
                                    </span>
                                )}
                                <span className='teacher-assignment-field__teacher-name'>
                                    {teacherName}
                                </span>
                                <button
                                    type='button'
                                    className='teacher-assignment-field__remove'
                                    aria-label={`Unassign ${teacherName}`}
                                    disabled={saving}
                                    onClick={() => removeTeacher(teacherId)}
                                >
                                    ×
                                </button>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className='teacher-assignment-field__empty'>
                    No teachers assigned yet.
                </div>
            )}

            {error && (
                <div className='teacher-assignment-field__error' role='alert'>
                    {error}
                </div>
            )}
        </section>
    )
}

export default TeacherAssignmentField

